import { useEffect, useState } from "react";
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  Clock,
  Copy,
  XCircle,
} from "lucide-react";

const TERMINAL_STATUSES = new Set([
  "closed",
  "cancelled",
  "rejected",
  "duplicate",
]);

function formatDelta(ms) {
  const abs = Math.abs(ms);
  const d = Math.floor(abs / 86400000);
  const h = Math.floor((abs % 86400000) / 3600000);
  const m = Math.floor((abs % 3600000) / 60000);
  const s = Math.floor((abs % 60000) / 1000);

  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m ${s.toString().padStart(2, "0")}s`;

  return `${m}m ${s.toString().padStart(2, "0")}s`;
}

function terminalDisplay(status, deadline, completedAt) {
  if (status === "cancelled") {
    return {
      label: "Cancelled",
      icon: Ban,
      classes: "text-slate-300 bg-slate-500/10 border-slate-500/25",
    };
  }

  if (status === "rejected") {
    return {
      label: "Rejected",
      icon: XCircle,
      classes: "text-rose-300 bg-rose-500/10 border-rose-500/30",
    };
  }

  if (status === "duplicate") {
    return {
      label: "Duplicate",
      icon: Copy,
      classes: "text-slate-300 bg-slate-500/10 border-slate-500/25",
    };
  }

  if (status === "closed") {
    const dueMs = deadline ? new Date(deadline).getTime() : NaN;
    const completedMs = completedAt ? new Date(completedAt).getTime() : NaN;

    if (
      Number.isFinite(dueMs) &&
      Number.isFinite(completedMs) &&
      completedMs > dueMs
    ) {
      return {
        label: "SLA Breached",
        icon: AlertTriangle,
        classes: "text-rose-300 bg-rose-500/10 border-rose-500/30",
      };
    }

    return {
      label: "SLA Met",
      icon: CheckCircle2,
      classes: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
    };
  }

  return null;
}

export default function SLATimer({
  deadline,
  label = "Resolution SLA",
  warnPercent = 20,
  compact = false,
  status,
  completedAt,
}) {
  const isTerminal = TERMINAL_STATUSES.has(status);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (isTerminal || !deadline) return undefined;

    const id = setInterval(() => setNow(Date.now()), 1000);

    return () => clearInterval(id);
  }, [isTerminal, deadline]);

  if (isTerminal) {
    const finalState = terminalDisplay(status, deadline, completedAt);

    if (!finalState) return null;

    const FinalIcon = finalState.icon;

    return (
      <div
        data-testid="sla-timer-final"
        className={`inline-flex items-center gap-2 rounded-md border px-2.5 py-1 font-mono text-xs ${finalState.classes}`}
      >
        <FinalIcon className="h-3.5 w-3.5" />
        {!compact && (
          <span className="uppercase tracking-widest text-[10px] opacity-70">
            {label}
          </span>
        )}
        <span>{finalState.label}</span>
      </div>
    );
  }

  if (!deadline) {
    return (
      <span
        className="text-xs text-muted-foreground"
        data-testid="sla-timer-none"
      >
        {compact ? "—" : `${label}: not set`}
      </span>
    );
  }

  const dueMs = new Date(deadline).getTime();
  const diff = dueMs - now;
  const breached = diff < 0;
  const warnMs =
    15 * 60 * 1000 * (100 / Math.max(warnPercent, 1));
  const warning = !breached && diff < warnMs;

  const color = breached
    ? "text-rose-300 bg-rose-500/10 border-rose-500/30"
    : warning
      ? "text-amber-300 bg-amber-500/10 border-amber-500/25"
      : "text-[#e5c158] bg-[#d4af37]/10 border-[#d4af37]/25";

  return (
    <div
      data-testid="sla-timer"
      className={`inline-flex items-center gap-2 rounded-md border px-2.5 py-1 font-mono text-xs ${color}`}
    >
      {breached ? (
        <AlertTriangle className="h-3.5 w-3.5" />
      ) : (
        <Clock className="h-3.5 w-3.5" />
      )}

      {!compact && (
        <span className="uppercase tracking-widest text-[10px] opacity-70">
          {label}
        </span>
      )}

      <span>
        {breached ? "-" : ""}
        {formatDelta(diff)}
      </span>
    </div>
  );
}
